// composables/useAuth.ts
import { useAppStore } from '@S/appStore.ts'
import { useUserDataStore } from '@S/userDataStore.ts'
import { useDashDataStore } from '@S/dashDataStore.ts'
import { useAppLink } from '@S/appLink'

type LogoutOptions = {
    redirectTo?: string
    silent?: boolean
}

type SessionCheckOK = {
    ok: true
    userId: number
    role?: string | null
    isVerified?: boolean
    isSuspended?: boolean
    suspendedReason?: string | null
    suspendedAt?: string | null
    authVersion?: number
}
type SessionCheckErr = { statusCode: number; statusMessage: string }

export const SUSPENDED_PATH = '/suspended'
export const CONTACT_PATH = '/contact'
export const LOGIN_PATH = '/auth/login'
export const REGISTER_PATH = '/auth/register'

export const isAuthRoute = (p: string) => p === LOGIN_PATH || p === REGISTER_PATH
export const isPublicSuspensionRoute = (p: string) => p === SUSPENDED_PATH || p === CONTACT_PATH

function safeToast(opts: any) {
    try {
        const n = (useNotifier as any)?.()
        if (n?.toast) n.toast(opts)
    } catch { }
}

function decodeJwtPayload<T = any>(token?: string | null): T | null {
    try {
        if (!token || typeof token !== 'string') return null

        const parts = token.split('.')
        if (parts.length < 2 || !parts[1]) return null

        const b64 = parts[1]
            .replace(/-/g, '+')   // URL-safe -> standard base64
            .replace(/_/g, '/')

        // pad base64 if needed
        const padLen = (4 - (b64.length % 4)) % 4
        const b64p = b64 + '='.repeat(padLen)

        let json: string
        if (typeof window === 'undefined') {
            // server-side
            json = Buffer.from(b64p, 'base64').toString('utf8')
        } else {
            // client-side
            json = decodeURIComponent(
                Array.prototype.map
                    .call(atob(b64p), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            )
        }

        return JSON.parse(json) as T
    } catch {
        return null
    }
}

export function useAuth() {
    const store = useAppStore()
    const link = useAppLink()
    const userDataStore = useUserDataStore()
    const dashDataStore = useDashDataStore()
    const { authToken, setAuthToken } = useACookie()

    const isLoggingOut = ref(false)

    /** Validate current token and fetch suspension/version details. */
    const sessionCheck = async (): Promise<SessionCheckOK | SessionCheckErr> => {
        if (!authToken.value) return { statusCode: 401, statusMessage: 'No token' }
        const ts = Date.now()
        return await $fetch<SessionCheckOK | SessionCheckErr>(`/api/v1/auth/sessioncheck?ts=${ts}`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${authToken.value}` },
        })
    }

    /** Logout endpoint + local clear. */
    const logout = async (opts: LogoutOptions = {}) => {
        if (isLoggingOut.value) return
        isLoggingOut.value = true

        try {
            if (authToken.value) {
                await useFetch('/api/v1/auth/logout', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${authToken.value}` },
                })
            }

            clearSession()

            if (!opts.silent) {
                safeToast({
                    icon: '$mdiCheck',
                    type: 'success',
                    title: 'Success',
                    text: 'Successfully logged out.',
                    color: 'green',
                })
            }

            if (opts.redirectTo) {
                await navigateTo(opts.redirectTo)
            }
        } catch (err) {
            console.error('Logout error:', err)
            if (!opts.silent) {
                safeToast({
                    icon: '$mdiAlert',
                    type: 'error',
                    title: 'Error',
                    text: 'Logout unsuccessful, error occurred.',
                    color: 'red',
                })
            }
        } finally {
            isLoggingOut.value = false
        }
    }

    const hydrateFromSession = (res: SessionCheckOK | SessionCheckErr): res is SessionCheckOK => {
        if ('statusCode' in res) {
            clearSession()
            link.clearAuthVersions()
            return false
        }
        const p = res as SessionCheckOK

        // server av
        const serverAv = Number((p as any).authVersion ?? 0)
        // jwt av
        const jwt = decodeJwtPayload<{ av?: number }>(authToken.value)
        const localAv = Number(jwt?.av ?? NaN)

        link.setAuthVersions(Number.isFinite(serverAv) ? serverAv : null,
            Number.isFinite(localAv) ? localAv : null)

        // UX quick check (server still enforces in guards)
        if (link.authVersion !== null && link.tokenAv !== null && link.authVersion !== link.tokenAv) {
            clearSession()
            link.clearAuthVersions()
            safeToast?.({
                icon: '$mdiLogout',
                type: 'info',
                title: 'Session ended',
                text: 'You were signed out because your session changed.',
                color: 'orange',
            })
            return false
        }

        store.isAuthenticated = true
            ; (store as any).userId = p.userId
            ; (store as any).role = p.role ?? null
            ; (store as any).isSuspended = !!p.isSuspended
            ; (store as any).suspendedReason = p.suspendedReason ?? null
            ; (store as any).suspendedAt = p.suspendedAt ?? null
        return true
    }

    const clearSession = () => {
        setAuthToken(null)
        store.isAuthenticated = null as any
            ; (store as any).userId = null
            ; (store as any).role = null
            ; (store as any).isSuspended = null
            ; (store as any).suspendedReason = null
            ; (store as any).suspendedAt = null
        userDataStore.resetUserData()
        dashDataStore.resetDashData()
        // also clear volatile auth versions
        link.clearAuthVersions()
    }

    return {
        // session helpers
        sessionCheck,
        hydrateFromSession,
        clearSession,

        // auth actions
        logout,

        // state
        isLoggingOut,

        // tokens
        authToken,

        // route helpers (for guards/middleware)
        SUSPENDED_PATH,
        CONTACT_PATH,
        LOGIN_PATH,
        REGISTER_PATH,
        isAuthRoute,
        isPublicSuspensionRoute,
    }
}
